/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Scheduling rules and helpers — pure functions, no I/O.
 *
 * Day model: the customer picks a working DAY (capacity DAILY_VISIT_CAP);
 * there are no clock-slot windows. Saturdays count as working days.
 * Sundays and SG public holidays are closed.
 *
 * Earliest available date = 2 working days of notice, counted from the LATER
 * of the first payment on the source quote and today. Working days exclude
 * Sundays and SG public holidays.
 */

import { isPublicHoliday } from './holidays.ts';
import { creditableLedgerEntries } from './creditableLedger.ts';

/**
 * Maximum measurement visits bookable on a single working day. The customer
 * picks a day (not a clock time); the route optimizer sequences the day's
 * visits into positions 1..DAILY_VISIT_CAP. Saturday shares the same cap (no
 * clock times means the historical half-day distinction is moot).
 */
export const DAILY_VISIT_CAP = 8;

/**
 * Day numbers per Date.getDay():
 *   0 = Sunday, 1-5 = Mon-Fri, 6 = Saturday.
 *
 * A working day is any non-Sunday that is also not on the SG public holiday
 * list. Saturday counts as a working day (we run a partial Saturday schedule).
 */
export const isWorkingDay = (date: Date): boolean => date.getDay() !== 0 && !isPublicHoliday(date);

/** Add `days` calendar days to a Date, preserving local-time. */
const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Add `n` working days, skipping Sundays. The starting date itself does NOT
 * count — if today is Monday and n=2, returns Wednesday. If today is Friday
 * and n=2, returns Tuesday (skipping Sunday).
 */
export const addWorkingDays = (start: Date, n: number): Date => {
  let cursor = new Date(start);
  let added = 0;
  while (added < n) {
    cursor = addDays(cursor, 1);
    if (isWorkingDay(cursor)) added++;
  }
  return cursor;
};

/** Format a Date as YYYY-MM-DD using local time. */
export const formatDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Parse YYYY-MM-DD as a local-time Date at midnight. */
export const parseDateKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d);
};

export interface PaymentLikeEntry {
  amount: number;
  timestamp: number;
  quarantined?: boolean;
}

/** Strip a Date to local midnight, so working-day arithmetic is calendar-aligned. */
const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Compute the earliest measurement date bookable against a quote's payment
 * ledger. Rule: **2 working days of notice, counted from the LATER of the
 * first credited payment and today.**
 *
 * The two-working-day lead time is operational notice for the crew, so it runs
 * from the moment someone is BOOKING, not from when the deposit happened to
 * land. Counting from the payment alone returned a date in the past on any
 * older order — `buildAvailableDays` then enumerated forward from it and the
 * picker's first offer was a day that had already gone, which
 * `bookMeasurementSlot` (sharing this function via `shared/`) would have
 * accepted. Taking the later of the two keeps the deposit rule intact for a
 * fresh order while never offering a date the crew cannot service.
 *
 * `now` is injected so the rule is testable without faking the system clock;
 * every caller uses the default.
 *
 * Quarantined entries are filtered here (backlog #61): money the quote could
 * not accept must not start the scheduling clock. `bookMeasurementSlot` already
 * filters server-side via `creditableEntries`; this keeps the client mirrors
 * (CustomerScheduling, the merged MC page) agreeing with the CF instead of
 * offering a date it would reject.
 *
 * Returns null if no payment has been received yet.
 */
export const computeEarliestAvailableDate = (
  ledger: PaymentLikeEntry[] | undefined,
  now: Date = new Date(),
): Date | null => {
  const credited = creditableLedgerEntries(ledger);
  if (credited.length === 0) return null;
  const firstPaymentTs = Math.min(...credited.map((e) => e.timestamp));
  const dayOfPayment = startOfDay(new Date(firstPaymentTs));
  const today = startOfDay(now);
  const clockStart = dayOfPayment.getTime() > today.getTime() ? dayOfPayment : today;
  return addWorkingDays(clockStart, 2);
};

export interface AvailableDay {
  /** YYYY-MM-DD */
  dateKey: string;
  /** Localised display string e.g. "Wed, 6 May" */
  display: string;
  /** Day-of-week 0-6. */
  dow: number;
}

/**
 * Build a list of selectable days starting from `earliest`. Skips Sundays
 * and SG public holidays. Targets roughly two weeks of working days but
 * scans up to ~5 calendar weeks to absorb long holiday stretches like CNY.
 */
export const buildAvailableDays = (
  earliest: Date,
  targetWorkingDays: number = 14,
): AvailableDay[] => {
  const out: AvailableDay[] = [];
  let cursor = new Date(earliest);
  let scanned = 0;
  const maxScan = 35;
  while (out.length < targetWorkingDays && scanned < maxScan) {
    if (isWorkingDay(cursor)) {
      out.push({
        dateKey: formatDateKey(cursor),
        display: cursor.toLocaleDateString('en-SG', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        }),
        dow: cursor.getDay(),
      });
    }
    cursor = addDays(cursor, 1);
    scanned++;
  }
  return out;
};
